import { RecordValue } from "libs/schema";
import { createContext, useContext, useEffect, useMemo } from "react";
import { Subject } from "rxjs";
import { useClientEnvironment } from "~/environment/ClientEnvironmentContext";
import { createUseContextHook } from "~/utils/createUseContextHook";
import { ParentComponent } from "~/utils/type-helpers";

/* -------------------------------------------------------------------------------------------------
 * ThreadTimelineContext
 * -----------------------------------------------------------------------------------------------*/

export type TThreadTimelineContext = {
  collapseMessageEvents: Subject<"expand" | "collapse" | string>;
  /** `true` if this thread timeline is being quoted inside another thread */
  isQuoted?: boolean;
  initiallyFocusEntryId?: string;
  onMessageInView?: (message: RecordValue<"message">) => void;
};

export const ThreadTimelineContext = createContext<TThreadTimelineContext | null>(null);

export const useThreadTimelineContext = createUseContextHook(ThreadTimelineContext, "ThreadTimelineContext");

/* -------------------------------------------------------------------------------------------------
 * ParentThreadTimelineContextProvider
 * -----------------------------------------------------------------------------------------------*/

export const ParentThreadTimelineContextProvider: ParentComponent<{}> = (props) => {
  const environment = useClientEnvironment();
  const threadContext = useContext(ThreadTimelineContext);

  useEffect(() => {
    if (threadContext) return;
    environment.logger.error("[ParentThreadTimelineContextProvider] ThreadTimelineContext is null");
  }, [threadContext, environment]);

  const parentThreadContext = useMemo((): TThreadTimelineContext => {
    const collapseMessageEvents = threadContext?.collapseMessageEvents ?? new Subject<"expand" | "collapse" | string>();

    return {
      isQuoted: true,
      collapseMessageEvents,
    };
  }, [threadContext]);

  return <ThreadTimelineContext.Provider value={parentThreadContext}>{props.children}</ThreadTimelineContext.Provider>;
};
